import React from "react";
import { get, keyBy, map, sortBy, sumBy } from "lodash";
import pluralize from "pluralize";
import * as config from "src/config/search";
import { filterTypes } from "src/pages/SearchPage/SearchPage.config";
import { numberToLocaleString } from "src/util/toLocaleString";
import PersonSearchActions from "src/components/Search/PersonSearchActions";
import URL, { getSearchPath, url } from "src/consts/urlPaths";

import { getName, getInitials } from "src/util/person";
import { getPositionPerson, getPositionId } from "src/util/position";
import { getAllocationStatus } from "src/util/allocationStatus";
import { roundFte } from "src/util/roundingStrategy";
import { positionDetailsPath } from "src/util/routing";

export const SEARCH_SECTION = {
  PEOPLE: "people",
  POSITIONS: "positions",
  GROUPS: "groups",
  TAGS: "tags",
};

export const EMPTY_SEARCH_DATA = {
  result: {
    pagedPeople: { totalCount: 0, people: [] },
    pagedPositions: { totalCount: 0, positions: [] },
    pagedGroups: { totalCount: 0, groups: [] },
    pagedTags: { totalCount: 0, tags: [] },
  },
};

/**
 * Merge a membership/allocation update into in-memory globalSearch people.
 * Search stores results in React state (not a live Apollo watch), so allocate
 * from the modal must patch here for team chips + allocationStatus filters.
 */
export const patchPersonInGlobalSearchData = (searchData, updatedPerson) => {
  const people = searchData?.result?.pagedPeople?.people;
  if (!people?.length || !updatedPerson?.aggregateId) {
    return searchData;
  }

  const personIndex = people.findIndex(
    (person) => person.aggregateId === updatedPerson.aggregateId
  );
  if (personIndex === -1) {
    return searchData;
  }

  const nextPeople = people.map((person, index) =>
    index === personIndex ? { ...person, ...updatedPerson } : person
  );

  return {
    ...searchData,
    result: {
      ...searchData.result,
      pagedPeople: {
        ...searchData.result.pagedPeople,
        people: nextPeople,
      },
    },
  };
};

/**
 * Re-apply membership patches over a search payload (e.g. cache-first replay).
 * @param {object} searchData
 * @param {Map<string, object>|Iterable<[string, object]>|object[]} patches
 */
export const applyPersonPatchesToGlobalSearchData = (searchData, patches) => {
  if (!searchData || !patches) {
    return searchData;
  }

  const values =
    typeof patches.values === "function"
      ? patches.values()
      : Array.isArray(patches)
        ? patches
        : Object.values(patches);

  let next = searchData;
  for (const person of values) {
    next = patchPersonInGlobalSearchData(next, person);
  }
  return next;
};

const membershipFieldMatches = (personValue, patchValue) =>
  JSON.stringify(personValue ?? null) === JSON.stringify(patchValue ?? null);

/**
 * Drop overlay patches that should no longer stick.
 * - Matching person already has the patched allocations/memberOf → redundant.
 * - trustIncoming (network result): any person present drops their patch so
 *   later server data is not frozen under the last MEMBERSHIP_CHANGED payload.
 * Mutates and returns the same Map when given a Map.
 */
export const prunePersonPatchesAgainstSearchData = (
  patches,
  searchData,
  { trustIncoming = false } = {}
) => {
  if (!patches || typeof patches.delete !== "function") {
    return patches;
  }

  const people = searchData?.result?.pagedPeople?.people;
  if (!people?.length) {
    return patches;
  }

  for (const person of people) {
    const patch = patches.get(person.aggregateId);
    if (!patch) {
      continue;
    }

    if (trustIncoming) {
      patches.delete(person.aggregateId);
      continue;
    }

    const allocationsMatch =
      patch.allocations === undefined ||
      membershipFieldMatches(person.allocations, patch.allocations);
    const memberOfMatch =
      patch.memberOf === undefined ||
      membershipFieldMatches(person.memberOf, patch.memberOf);

    if (allocationsMatch && memberOfMatch) {
      patches.delete(person.aggregateId);
    }
  }

  return patches;
};

/**
 * Fold an incoming globalSearch payload into local state: skip duplicate-page
 * replay before pagination merge, then apply any remaining membership patches.
 */
export const mergeIncomingGlobalSearchResult = ({
  prevData,
  searchResult,
  patches,
}) => {
  if (JSON.stringify(searchResult) === JSON.stringify(prevData)) {
    return prevData;
  }

  const merged = getSearchResultsMergedWithPaginatedData(
    prevData,
    searchResult
  );
  return applyPersonPatchesToGlobalSearchData(merged, patches);
};

export const getScopedQueryVariables = (variables, section) => {
  return {
    ...variables,
    includePeople: section === SEARCH_SECTION.PEOPLE,
    includePositions: section === SEARCH_SECTION.POSITIONS,
    includeGroups: section === SEARCH_SECTION.GROUPS,
    includeTags: section === SEARCH_SECTION.TAGS,
  };
};

export const mergeSectionData = (data, section, partialData) => {
  const incomingResult = get(partialData, "result", {});
  const mergedData = {
    ...data,
    result: {
      ...data.result,
    },
  };

  if (section === SEARCH_SECTION.PEOPLE && incomingResult.pagedPeople) {
    mergedData.result.pagedPeople = incomingResult.pagedPeople;
  }
  if (section === SEARCH_SECTION.POSITIONS && incomingResult.pagedPositions) {
    mergedData.result.pagedPositions = incomingResult.pagedPositions;
  }
  if (section === SEARCH_SECTION.GROUPS && incomingResult.pagedGroups) {
    mergedData.result.pagedGroups = incomingResult.pagedGroups;
  }
  if (section === SEARCH_SECTION.TAGS && incomingResult.pagedTags) {
    mergedData.result.pagedTags = incomingResult.pagedTags;
  }

  return mergedData;
};

export const sanitiseString = (string) =>
  string ? string.replace(/[^a-zA-Z0-9 -]/g, "") : string;

export const getMemberCountDisplay = (memberCount) =>
  `${numberToLocaleString(memberCount)} ${pluralize("member", memberCount)}`;

const formatPositionDateRange = (startDate, endDate) => {
  if (!startDate && !endDate) {
    return null;
  }
  if (startDate && endDate) {
    return `${startDate} – ${endDate}`;
  }
  if (startDate) {
    return `${startDate} – Ongoing`;
  }
  return endDate;
};

// Occupancy is four-way (VACANT | PARTIALLY_FILLED | FILLED | OVER_FILLED) —
// any non-vacant status still just lists who's currently in seat.
const formatPositionOccupancyLabel = (position) => {
  if (position?.occupancyStatus === "VACANT" || !position?.occupancyStatus) {
    return "Open";
  }
  const incumbents = position.incumbents || [];
  if (incumbents.length === 0) {
    return "Filled";
  }
  return incumbents.map((person) => getName(person)).join(", ");
};

export const getPeopleResults = (peopleData, isLoading = false) => {
  return map(peopleData, (person, idx) => {
    const link = url(URL.PERSON_DETAILS_BASE_PATH, { id: person.aggregateId });
    const title = getName(person);
    const initials = getInitials(person);
    const { avatar: image, jobTitle: subtitle, aggregateId } = person;

    return {
      key: `person-${aggregateId}`,
      label: getName(person),
      type: config.searchEntityType.PERSON,
      value: {
        CustomActionsComponent: () => <PersonSearchActions person={person} />,
        attributes: get(person, "attributes"),
        badge: {
          title,
          subtitle,
          initials,
          image,
        },
        link,
        template: "optionWithAction",
        type: "people",
        aggregateId,
        person,
        isLoading,
        customStyles: idx === peopleData.length - 1 && peopleData.length < 3,
      },
    };
  });
};

export const getPositionResults = (positionsData, isLoading = false) => {
  return map(positionsData, (position, idx) => {
    const positionPerson = getPositionPerson(position);
    const positionId = getPositionId(position);
    const title =
      positionPerson?.preferredName?.trim() ||
      getName(positionPerson) ||
      positionId ||
      "Position";
    const dateLabel = formatPositionDateRange(
      position.startDate,
      position.endDate
    );
    const occupancyLabel = formatPositionOccupancyLabel(position);
    const link = positionDetailsPath({ id: positionId });
    // FILLED / OVER_FILLED seats read as their occupant; anything else (or a
    // missing status) still has open FTE and gets the vacant-seat avatar.
    const isOpenSeat =
      position.occupancyStatus !== "FILLED" &&
      position.occupancyStatus !== "OVER_FILLED";
    const occupant = position.incumbents?.[0];

    return {
      key: `position-${positionId}`,
      label: title,
      type: config.searchEntityType.POSITION,
      value: {
        badge: {
          title,
          subtitle: dateLabel,
          occupancyLabel,
          initials: isOpenSeat
            ? getInitials({ preferredName: title })
            : getInitials(occupant || { preferredName: title }),
          image: isOpenSeat
            ? undefined
            : occupant?.avatar || positionPerson?.avatar,
          isOpenSeat,
        },
        link,
        template: "optionPosition",
        type: "positions",
        position,
        isLoading,
        customStyles:
          idx === positionsData.length - 1 && positionsData.length < 3,
      },
    };
  });
};

export const getTeamResults = (groupData, showHiddenGroups = false) => {
  return map(groupData, (team, idx) => {
    const link = url(URL.TEAM_DETAILS_BASE_PATH, { id: team.id });
    const memberCount = getMemberCountDisplay(team.memberCount);

    return {
      key: `team-${team.id}`,
      label: team.name,
      type: config.searchEntityType.TEAM,
      value: {
        group: team,
        memberCount,
        link,
        showHiddenGroup: showHiddenGroups,
        template: "option",
        type: "teams",
        customStyles: idx === groupData.length - 1 && groupData.length < 3,
      },
    };
  });
};

export const getTagResults = (tags) => {
  return map(tags, (tag, idx) => {
    return {
      key: `tag-${tag.id}`,
      label: tag.displayValue,
      type: config.searchEntityType.TAG,
      value: {
        tag,
        link: url(URL.TAG_DETAILS, { id: tag.id }),
        template: "optionTag",
        type: "tags",
        customStyles: idx === tags.length - 1 && tags.length < 3,
      },
    };
  });
};

export const getOptionLink = (searchTerm, count, found, label, type) => {
  return {
    label: "",
    options: [
      {
        label,
        value: {
          url: getSearchPath(type, searchTerm),
          count,
          found,
          template: "link",
          type,
        },
      },
    ],
  };
};

export const getPersonSearchAfterId = (data) => {
  return get(data, "result.pagedPeople.searchAfterId");
};

export const hasMoreResults = (data, type) => {
  const peopleFound = get(data, "result.pagedPeople.people.length") || 0;
  const peopleCount = get(data, "result.pagedPeople.totalCount") || 0;
  const positionsFound =
    get(data, "result.pagedPositions.positions.length") || 0;
  const positionsCount = get(data, "result.pagedPositions.totalCount") || 0;
  const teamFound = get(data, "result.pagedGroups.groups.length") || 0;
  const teamCount = get(data, "result.pagedGroups.totalCount") || 0;
  const hasMorePeople = peopleFound < peopleCount;
  const hasMorePositions = positionsFound < positionsCount;
  const hasMoreTeams = teamFound < teamCount;

  if (type === filterTypes.PEOPLE) {
    return hasMorePeople;
  }
  if (type === filterTypes.POSITIONS) {
    return hasMorePositions;
  }
  if (type === filterTypes.TEAMS) {
    return hasMoreTeams;
  }
  if (type === filterTypes.ALL) {
    return hasMorePeople || hasMorePositions || hasMoreTeams;
  }
  return false;
};

export const getFormattedSearchResults = ({
  data,
  searchTerm,
  tagsSearchResult,
  isLoading = false,
  showHiddenGroups = false,
}) => {
  const people = getPeopleResults(
    get(data, "result.pagedPeople.people"),
    isLoading
  );
  const positions = getPositionResults(
    get(data, "result.pagedPositions.positions"),
    isLoading
  );
  const teams = getTeamResults(
    get(data, "result.pagedGroups.groups"),
    showHiddenGroups
  );

  const tags = getTagResults(
    get(tagsSearchResult, "tags") || get(data, "result.pagedTags.tags")
  );

  const peopleFound = get(data, "result.pagedPeople.people.length") || 0;
  const peopleCount = get(data, "result.pagedPeople.totalCount") || 0;
  const positionsFound =
    get(data, "result.pagedPositions.positions.length") || 0;
  const positionsCount = get(data, "result.pagedPositions.totalCount") || 0;
  const teamFound = get(data, "result.pagedGroups.groups.length") || 0;
  const teamCount = get(data, "result.pagedGroups.totalCount") || 0;
  const tagsFound =
    get(tagsSearchResult, "tags.length") ||
    get(data, "result.pagedTags.tags.length") ||
    0;
  const tagsCount =
    get(tagsSearchResult, "totalCount") ||
    get(data, "result.pagedTags.totalCount") ||
    0;

  const hasMorePeople = peopleFound < peopleCount;
  const hasMorePositions = positionsFound < positionsCount;
  const hasMoreTeams = teamFound < teamCount;
  const hasMoreTags = tagsFound < tagsCount;

  const peopleLink = getOptionLink(
    searchTerm,
    peopleCount,
    peopleFound,
    "People Link",
    "people"
  );

  const positionsLink = getOptionLink(
    searchTerm,
    positionsCount,
    positionsFound,
    "Positions Link",
    "positions"
  );

  const teamLink = getOptionLink(
    searchTerm,
    teamCount,
    teamFound,
    "Team Link",
    "teams"
  );

  const tagSearchPageLink = getOptionLink(
    searchTerm,
    tagsCount,
    tagsFound,
    "Tag Link",
    "tags"
  );

  return {
    people: {
      label: "People",
      options: people,
      counters: { total: peopleCount, found: peopleFound },
      link: hasMorePeople ? peopleLink : null,
      hasMore: hasMorePeople,
    },
    positions: {
      label: "Positions",
      options: positions,
      counters: { total: positionsCount, found: positionsFound },
      link: hasMorePositions ? positionsLink : null,
      hasMore: hasMorePositions,
    },
    teams: {
      label: "Teams",
      options: teams,
      counters: { total: teamCount, found: teamFound },
      link: hasMoreTeams ? teamLink : null,
      hasMore: hasMoreTeams,
    },
    tags: {
      label: "Tags",
      options: tags,
      counters: { total: tagsCount, found: tagsFound },
      link: hasMoreTags ? tagSearchPageLink : null,
      hasMore: hasMoreTags,
    },
    totals: {
      total:
        (peopleCount || 0) +
        (positionsCount || 0) +
        (teamCount || 0) +
        (tagsCount || 0),
      found:
        (peopleFound || 0) +
        (positionsFound || 0) +
        (teamFound || 0) +
        (tagsFound || 0),
    },
  };
};

export const getSearchResultsMergedWithPaginatedData = (prevData, newData) => {
  if (prevData && !newData) {
    return prevData;
  }
  if (!prevData && newData) {
    return newData;
  }

  const prevPeople = get(prevData, "result.pagedPeople.people");
  const newPeople = get(newData, "result.pagedPeople.people", []);
  const prevPositions = get(prevData, "result.pagedPositions.positions");
  const newPositions = get(newData, "result.pagedPositions.positions", []);
  const prevGroups = get(prevData, "result.pagedGroups.groups");
  const newGroups = get(newData, "result.pagedGroups.groups", []);
  const prevTags = get(prevData, "result.pagedTags.tags");
  const newTags = get(newData, "result.pagedTags.tags", []);

  const result = { ...newData.result };

  if (prevPeople) {
    result.pagedPeople = {
      ...newData.result.pagedPeople,
      people: [...prevPeople, ...newPeople],
    };
  }

  if (prevPositions) {
    result.pagedPositions = {
      ...newData.result.pagedPositions,
      positions: [...prevPositions, ...newPositions],
    };
  }

  if (prevGroups) {
    result.pagedGroups = {
      ...newData.result.pagedGroups,
      groups: [...prevGroups, ...newGroups],
    };
  }

  if (prevTags) {
    result.pagedTags = {
      ...newData.result.pagedTags,
      tags: [...prevTags, ...newTags],
    };
  }

  return {
    ...prevData,
    result,
  };
};

export const createPlaceholderData = (length) => {
  const getOptions = (type) =>
    Array.from({ length }, (_, i) => ({
      key: `placeholder-${type}-${i}`,
      value: {},
      type,
    }));

  return [
    {
      options: getOptions(config.searchEntityType.PERSON),
    },
    {
      options: getOptions(config.searchEntityType.POSITION),
    },
    {
      options: getOptions(config.searchEntityType.TEAM),
    },
    {
      options: getOptions(config.searchEntityType.TAG),
    },
  ];
};

export const highlightSelectedString = (text, highlights) => {
  if (!text) {
    return text;
  }

  if (highlights === undefined) {
    return text;
  }

  if (highlights.length < 2) {
    return text;
  }

  // TODO: escape properly and make work with multiple terms
  const highlightsEscaped = highlights.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(highlightsEscaped, "gi");

  const style = "background: rgba(0,122,196,0.1);";

  return React.createElement("div", {
    style: { lineHeight: "1.2rem" },
    "data-testid": text,
    dangerouslySetInnerHTML: {
      __html: text.replace(regex, `<span style="${style}">$&</span>`),
    },
  });
};

export const teamsToRender = (person, groupTypes) => {
  const supplyTeams = [];
  const demandTeams = [];
  const allocationByTeamId = keyBy(person?.allocations, "targetGroupId");

  map(person?.memberOf, (item) => {
    const groupType = groupTypes[item.type];

    if (groupType?.isSupply) {
      supplyTeams.push({
        ...item,
        ...(Number.isFinite(person?.fte) && { membershipFte: person.fte }),
      });
    }

    if (groupType?.isDemand) {
      const allocatedFte = allocationByTeamId[item.id]?.fte;
      demandTeams.push({
        ...item,
        ...(Number.isFinite(allocatedFte) && { membershipFte: allocatedFte }),
      });
    }
  });

  return [...sortBy(supplyTeams, ["name"]), ...sortBy(demandTeams, ["name"])];
};

export const getPersonAvailableFte = (person) =>
  roundFte((person?.fte || 0) - sumBy(person?.allocations, "fte"));

export const getPersonAllocationState = getAllocationStatus;
